import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { auditApi } from '../services/auditApi';
import { withTimeout } from '../services/dashboardApi';
import { AdminFeatures } from '../types/audit';

export interface FeatureFlags extends AdminFeatures {
  /** Spec 070 — the business status strip; off unless the features call says `true`. */
  businessStatus: boolean;
  /** False until the switches have been read (or failed); pages that branch on a switch wait for it. */
  loaded: boolean;
}

const OFF: FeatureFlags = { dashboardV2: false, auditScreen: false, businessStatus: false, loaded: true };

const FeatureFlagsContext = createContext<FeatureFlags>(OFF);

/**
 * Spec 069 §4 — the Runtime Config switches (`garagat.admin-dashboard.v2-enabled`,
 * `garagat.admin-audit.screen-enabled`, and Spec 070's `garagat.admin-dashboard.business-status-enabled`), read once per session from `GET /api/admin/audit/features`
 * (any admin). Any failure, or no answer within 3 s (a hung users service), leaves all off: the
 * portal then behaves exactly as before Spec 069 and the landing page never waits on it.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth();
  const [flags, setFlags] = useState<FeatureFlags>({ dashboardV2: false, auditScreen: false, businessStatus: false, loaded: false });
  const requested = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setFlags({ dashboardV2: false, auditScreen: false, businessStatus: false, loaded: false });
      requested.current = false;
      return;
    }
    // Once per session: a token refresh must not re-read (or flicker) the switches.
    if (requested.current) return;
    requested.current = true;
    withTimeout((signal) => auditApi.features(token, signal))
      .then((f) => setFlags({
        dashboardV2: !!f.dashboardV2,
        auditScreen: !!f.auditScreen,
        businessStatus: f.businessStatus === true,
        loaded: true,
      }))
      .catch(() => setFlags(OFF));
  }, [token, isAuthenticated]);

  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>;
}

/** Outside the provider (unit tests of unrelated screens) both switches read off. */
export function useFeatureFlags(): FeatureFlags {
  return useContext(FeatureFlagsContext);
}
